const CAP50 = {
  "id": 216,
  "title": "Canales de Pago",
  "slug": "canales-de-pago",
  "type": "lesson",
  "category": "bitcoin-avanzado",
  "level": "intermediate",
  "preview": "Los canales de pago son un componente esencial de la escalabilidad de Bitcoin. Permiten realizar transacciones rápidas, privadas y económicas fuera de la cadena principal, manteniendo la seguridad de Bitcoin.",
  "sections": [
    {
      "heading": "Introducción",
      "text": [
        "Los canales de pago son una solución diseñada para permitir que dos usuarios realicen múltiples transacciones entre ellos sin saturar la cadena principal de Bitcoin.",
        "La idea central es simple: abrir un canal mediante una transacción en la blockchain, ejecutar pagos fuera de ella y cerrar el canal registrando únicamente el estado final.",
        "Este mecanismo es la base de Lightning Network, la solución de capa 2 más extendida de Bitcoin."
      ]
    },

    {
      "heading": "¿Qué es un canal de pago?",
      "text": [
        "Un canal de pago es una estructura criptográfica que permite a dos partes enviar y recibir Bitcoin entre sí sin necesidad de que cada transacción quede registrada en la blockchain.",
        "En la cadena principal solo se registran dos operaciones clave:",
        "- La apertura del canal.",
        "- El cierre del canal.",
        "Todo lo que ocurre entre ambas etapas sucede fuera de la cadena (off-chain), lo que permite una velocidad prácticamente instantánea y un uso intensivo sin congestionar la red."
      ]
    },

    {
      "heading": "Cómo funciona un canal de pago en Bitcoin",
      "text": [
        "1. Creación del canal (funding transaction): ambos participantes bloquean una cantidad de BTC en una dirección multifirma (normalmente 2-de-2). Esta transacción queda registrada en la blockchain y el balance bloqueado será el capital disponible dentro del canal.",
        "2. Intercambio de transacciones fuera de la cadena: una vez abierto el canal, los usuarios pueden intercambiar pagos firmados que representan el estado actualizado de los saldos. Cada nuevo pago invalida el anterior mediante mecanismos criptográficos de penalización. Estas transacciones no se publican en la blockchain, lo que evita congestión y comisiones elevadas.",
        "3. Cierre del canal: en cualquier momento, uno o ambos participantes pueden cerrar el canal enviando la última transacción firmada a la blockchain. La red de Bitcoin liquidará los saldos finales según el último estado válido. El canal funciona, en la práctica, como una mini-contabilidad privada verificada criptográficamente."
      ]
    },

    {
      "heading": "Tipos de canales de pago",
      "text": [
        "1. Canales unidireccionales: permiten que una de las partes envíe pagos, pero no reciba. Fueron los primeros modelos teóricos de canales de pago. Hoy en día son poco utilizados porque los canales modernos son bidireccionales.",
        "2. Canales bidireccionales: ambas partes pueden enviar y recibir BTC a través del mismo canal. Este es el modelo esencial para Lightning Network, ya que permite que los usuarios actualicen el saldo del canal en ambas direcciones sin necesidad de abrir un canal nuevo.",
        "3. Canales enrutados (Lightning Network): los usuarios no necesitan abrir un canal directo con cada persona con la que desean operar. Lightning utiliza nodos interconectados para enrutar pagos entre usuarios que no confían entre sí. Esto convierte la red en un sistema global de micropagos sobre Bitcoin."
      ]
    },

    {
      "heading": "Tabla comparativa: transacciones on-chain vs canales de pago (Lightning)",
      "table": {
        "columns": [
          "Aspecto",
          "Transacciones on-chain (L1)",
          "Canales de pago / Lightning (L2)"
        ],
        "rows": [
          [
            "Registro de las operaciones",
            "Cada transacción queda grabada permanentemente en la blockchain de Bitcoin.",
            "Solo la apertura y el cierre del canal se registran en la blockchain; los pagos intermedios quedan fuera de ella."
          ],
          [
            "Velocidad",
            "Limitada por el tiempo de bloque (unos 10 minutos) y la congestión de la red.",
            "Pagos prácticamente instantáneos, sin depender de nuevos bloques."
          ],
          [
            "Comisiones",
            "Pueden ser elevadas en momentos de alta demanda, ya que se compite por espacio en el bloque.",
            "Muy bajas o cercanas a cero por cada pago dentro del canal; solo se paga on-chain al abrir y cerrar."
          ],
          [
            "Escalabilidad",
            "La capacidad está limitada por el tamaño de bloque y el número de transacciones por segundo.",
            "Permite manejar un volumen muy elevado de micropagos sin saturar la red principal."
          ],
          [
            "Privacidad",
            "Las transacciones son públicas y pueden analizarse en el explorador de bloques.",
            "Los pagos dentro del canal no se publican individualmente, lo que mejora la privacidad."
          ],
          [
            "Casos de uso ideales",
            "Movimientos de mayor valor, liquidaciones finales, ahorro y custodia a largo plazo.",
            "Compras cotidianas, micropagos, servicios por uso, pagos frecuentes entre las mismas partes."
          ],
          [
            "Requisitos técnicos",
            "Uso sencillo: enviar y recibir a direcciones on-chain.",
            "Requiere gestionar canales, liquidez y, en muchos casos, mantener nodos o usar wallets compatibles con Lightning."
          ]
        ]
      }
    },

    {
      "heading": "Ventajas de los canales de pago",
      "list": [
        "Los canales de pago ofrecen beneficios fundamentales para la evolución de Bitcoin:",
        "1. Comisiones muy bajas: las operaciones off-chain prácticamente no tienen costo por transacción.",
        "2. Pagos instantáneos: no es necesario esperar confirmaciones de bloques.",
        "3. Privacidad mejorada: las transacciones no se publican individualmente en la blockchain.",
        "4. Escalabilidad casi infinita: millones de pagos pueden registrarse fuera de la cadena principal.",
        "5. Compatibilidad total con Bitcoin: los canales se basan en su seguridad y en su modelo UTXO, sin modificar las reglas básicas del protocolo."
      ]
    },

    {
      "heading": "Seguridad en los canales de pago",
      "list": [
        "Los canales de pago están diseñados con mecanismos criptográficos robustos para reducir la necesidad de confianza:",
        "- Firmas múltiples (multi-sig) para bloquear fondos al abrir el canal.",
        "- Transacciones de compromiso que actualizan los saldos de forma segura y verificable.",
        "- Mecanismos de penalización: si un participante intenta transmitir un estado antiguo, puede perder sus fondos bloqueados.",
        "- Tiempos bloqueados (timelocks) que limitan intentos de cierre malicioso.",
        "Gracias a estas protecciones, los canales permiten operar de forma segura incluso entre partes que no se conocen."
      ]
    },

    {
      "heading": "Limitaciones y desafíos",
      "text": [
        "Aunque los canales de pago son una mejora enorme para la escalabilidad, presentan algunos retos prácticos:",
        "- Es necesario tener liquidez en ambos extremos del canal para enrutar pagos de forma eficiente.",
        "- Operar canales y nodos requiere cierto conocimiento técnico y herramientas específicas.",
        "- Los nodos deben permanecer en línea o apoyarse en servicios de vigilancia (watchtowers) para supervisar intentos de fraude.",
        "- No están pensados para grandes montos únicos, sino para micropagos o pagos frecuentes y rápidos.",
        "A pesar de estas limitaciones, hoy son una de las soluciones más prácticas y coherentes con la filosofía descentralizada de Bitcoin."
      ]
    },

    {
      "heading": "Relación entre canales de pago y Lightning Network",
      "text": [
        "Lightning Network es la implementación más extendida basada en canales de pago.",
        "Funciona como una red global donde miles de nodos mantienen canales entre sí y enrutan pagos usando caminos intermedios.",
        "Esto permite que dos usuarios que no tienen un canal directo puedan pagarse mutuamente a través de nodos conectores.",
        "Gracias a este modelo, Lightning permite escalar Bitcoin a niveles comparables con sistemas financieros globales, conservando la seguridad y las garantías del protocolo base."
      ]
    },

    {
      "heading": "Conclusión",
      "text": [
        "Los canales de pago representan una de las innovaciones más importantes en la historia de Bitcoin.",
        "Permiten realizar transacciones rápidas, privadas y económicas sin modificar el protocolo principal ni comprometer su seguridad.",
        "Con Lightning Network como su manifestación más potente, los canales de pago se han convertido en una pieza clave para que Bitcoin pueda utilizarse a escala global en el día a día."
      ]
    }
  ]
};

export default CAP50;
